import {
  AddEquation,
  type Camera,
  CustomBlending,
  NeutralToneMapping,
  OneFactor,
  OneMinusSrcAlphaFactor,
  type Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  RenderPipeline,
  Scene,
  SRGBColorSpace,
  type ToneMapping,
} from "three/webgpu";
import * as TSL from "three/tsl";
import { bloom as bloomNode } from "three/addons/tsl/display/BloomNode.js";
import { outline as outlineNode } from "three/addons/tsl/display/OutlineNode.js";
import type { Color } from "../common/color.js";
import type { GpuContext } from "../engine/GpuContext.js";
import type { RenderTarget } from "../engine/RenderTarget.js";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
const T = TSL as N;

export interface BloomOptions {
  /** How much light the bloom adds. */
  strength?: number;
  /** How far it spreads, 0..1. */
  radius?: number;
  /** Luminance below which nothing blooms. */
  threshold?: number;
}

export interface OutlineOptions {
  /** The objects outlined, and everything under them. */
  objects: Object3D[];
  color: Color;
  /** Pixels. */
  thickness?: number;
}

export interface SceneRendererOptions {
  scene?: Scene;
  camera?: PerspectiveCamera | OrthographicCamera;
  /** What shows where the scene has nothing. Transparent by default, so whatever is behind shows. */
  clear?: Color | null;
  /** three's tone mapping. Neutral by default: colors as authored, highlights rolled off rather than clipped. */
  toneMapping?: ToneMapping;
  exposure?: number;
  bloom?: BloomOptions | null;
}

const TRANSPARENT: Color = { r: 0, g: 0, b: 0, a: 0 };

/**
 * Renders a three.js scene through a camera: lit in linear light, finished - bloom, an outline - and
 * brought to display values, into a `RenderTarget` the UI can show or straight onto the game's frame.
 *
 * The finishing step is what makes a scene sit right beside the UI. The renderer runs without color
 * management because the UI is authored in display values; a lit scene is not, so it is tone mapped and
 * encoded to sRGB here, as the last thing it does, rather than by the renderer for everything.
 */
export class SceneRenderer {
  readonly gpu: GpuContext;
  scene: Scene;
  camera: PerspectiveCamera | OrthographicCamera;
  clear: Color | null;

  #pipeline: RenderPipeline;
  #toneMapping: ToneMapping;
  #exposure: number;
  #bloom: BloomOptions | null;
  #outline: OutlineOptions | null = null;
  #outlineColor = T.uniform(T.color(1, 1, 1));
  /** What the pipeline was built for, so it is rebuilt only when that changes. */
  #built: { scene: Scene; camera: Camera; bloom: boolean; outline: boolean; toneMapping: ToneMapping } | null = null;
  #bloomPass: N = null;
  #outlinePass: N = null;

  constructor(gpu: GpuContext, options: SceneRendererOptions = {}) {
    this.gpu = gpu;
    this.scene = options.scene ?? new Scene();
    this.camera = options.camera ?? new PerspectiveCamera(50, 1, 0.1, 500);
    this.clear = options.clear === undefined ? TRANSPARENT : options.clear;
    this.#toneMapping = options.toneMapping ?? NeutralToneMapping;
    this.#exposure = options.exposure ?? 1;
    this.#bloom = options.bloom ?? null;
    this.#pipeline = new RenderPipeline(gpu.renderer);
    this.#pipeline.outputColorTransform = false;
    // The scene comes out premultiplied - it was blended over a transparent clear - so it goes over
    // whatever is beneath it that way, which onto a cleared target is the same as replacing it.
    const material = (this.#pipeline as unknown as { _quadMesh: { material: N } })._quadMesh.material;
    material.transparent = true;
    material.blending = CustomBlending;
    material.blendEquation = material.blendEquationAlpha = AddEquation;
    material.blendSrc = material.blendSrcAlpha = OneFactor;
    material.blendDst = material.blendDstAlpha = OneMinusSrcAlphaFactor;
  }

  get bloom(): BloomOptions | null {
    return this.#bloom;
  }
  set bloom(v: BloomOptions | null) {
    this.#bloom = v;
    this.#applyBloom();
  }

  /** An outline round some objects, or null for none. */
  get outline(): OutlineOptions | null {
    return this.#outline;
  }
  set outline(v: OutlineOptions | null) {
    this.#outline = v && v.objects.length > 0 ? v : null;
    if (this.#outline && this.#outlinePass) {
      this.#outlinePass.selectedObjects = this.#outline.objects;
      this.#outlineColor.value.setRGB(this.#outline.color.r, this.#outline.color.g, this.#outline.color.b, SRGBColorSpace);
    }
  }

  get toneMapping(): ToneMapping {
    return this.#toneMapping;
  }
  set toneMapping(v: ToneMapping) {
    this.#toneMapping = v;
  }

  get exposure(): number {
    return this.#exposure;
  }
  set exposure(v: number) {
    this.#exposure = v;
  }

  /** Match a perspective camera's aspect to a surface `width` by `height`. */
  fit(width: number, height: number): void {
    const aspect = width / Math.max(1, height);
    if (this.camera instanceof PerspectiveCamera && this.camera.aspect !== aspect) {
      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
    }
  }

  #applyBloom(): void {
    const b = this.#bloom;
    if (!this.#bloomPass || !b) return;
    this.#bloomPass.strength.value = b.strength ?? 0.8;
    this.#bloomPass.radius.value = b.radius ?? 0.4;
    this.#bloomPass.threshold.value = b.threshold ?? 0.7;
  }

  #build(): void {
    const wantBloom = this.#bloom !== null;
    const wantOutline = this.#outline !== null;
    const built = this.#built;
    if (
      built &&
      built.scene === this.scene &&
      built.camera === this.camera &&
      built.bloom === wantBloom &&
      built.outline === wantOutline &&
      built.toneMapping === this.#toneMapping
    ) {
      return;
    }
    const scenePass = T.pass(this.scene, this.camera);
    let color: N = scenePass.getTextureNode("output");
    this.#bloomPass = null;
    this.#outlinePass = null;
    if (wantOutline) {
      const o = this.#outline!;
      this.#outlinePass = outlineNode(this.scene, this.camera, {
        selectedObjects: o.objects,
        edgeThickness: T.float(o.thickness ?? 1.5),
        edgeGlow: T.float(0),
      });
      this.#outlineColor.value.setRGB(o.color.r, o.color.g, o.color.b, SRGBColorSpace);
      const { visibleEdge, hiddenEdge } = this.#outlinePass;
      const edge = visibleEdge.add(hiddenEdge.mul(0.4));
      color = T.vec4(color.rgb.add(this.#outlineColor.mul(edge).mul(3)), T.max(color.a, edge));
    }
    if (wantBloom) {
      this.#bloomPass = bloomNode(color, 0.8, 0.4, 0.7);
      this.#applyBloom();
      color = T.vec4(color.rgb.add(this.#bloomPass.rgb), T.max(color.a, this.#bloomPass.a));
    }
    this.#pipeline.outputNode = T.renderOutput(color, this.#toneMapping, SRGBColorSpace);
    this.#pipeline.needsUpdate = true;
    this.#built = { scene: this.scene, camera: this.camera, bloom: wantBloom, outline: wantOutline, toneMapping: this.#toneMapping };
  }

  /**
   * Render the scene into `target`, cleared to `clear` - or, with no target, onto whatever the renderer is
   * pointed at, over what is already there.
   */
  render(target: RenderTarget | null = null): void {
    const renderer = this.gpu.renderer;
    if (target) this.fit(target.width, target.height);
    else this.fit(this.gpu.size.pixelWidth, this.gpu.size.pixelHeight);
    this.#build();

    const exposure = renderer.toneMappingExposure;
    renderer.toneMappingExposure = this.#exposure;
    const clear = this.clear ?? TRANSPARENT;
    renderer.setClearColor(this.gpu.threeColor(clear), clear.a);
    if (target) target.begin(TRANSPARENT);
    this.#pipeline.render();
    renderer.toneMappingExposure = exposure;
  }

  dispose(): void {
    this.#pipeline.dispose();
  }
}
